import { useEffect, useMemo } from 'react'

import Intercom from '@intercom/messenger-js-sdk'
import { OrganizationFragmentWithUsers, UserDataFragment } from 'types/graphql'

import { EnvVariables } from 'src/constants'
import { useGetIntercomJwt } from 'src/fetch/users'
import { useCurrentBreakpoint } from 'src/hooks/use-current-breakpoint'
import { formatUserName } from 'src/utils'

import { StateAbbreviationMap, StateNameMap } from '../constants/states'

import { useIsIntercomEnabled } from './use-is-intercom-enabled'

export const IntercomLauncherClassName = 'intercom-launcher-button'

// Intercom defaults 0 padding to the default horizontal padding that they use.
export const DefaultIntercomHorizontalPadding = 0

type Params = {
  me: UserDataFragment | null | undefined
  organization: OrganizationFragmentWithUsers | null | undefined
}

type IntercomCompany = {
  company_id: string
  name: string
  [key: string]: string
}

// https://developers.intercom.com/installing-intercom/web/attributes-objects#company-object
const getIntercomCompany = (organization: OrganizationFragmentWithUsers): IntercomCompany => {
  const stateName = StateNameMap[organization.state]
  const stateAbbreviation = StateAbbreviationMap[organization.state]
  return {
    company_id: organization.uuid,
    name: `${organization.city}, ${stateAbbreviation}`,
    display_name: organization.displayName ?? '',
    city: organization.city,
    state: stateName,
    state_code: stateAbbreviation,
    time_zone: organization.timeZone,
  }
}

export const useSetUpIntercom = ({ me, organization }: Params) => {
  const isIntercomEnabled = useIsIntercomEnabled()
  const stringifiedMe = me ? JSON.stringify(me) : ''
  const { data: intercomJwtData } = useGetIntercomJwt(isIntercomEnabled && !!me)
  const intercomJwt = intercomJwtData?.getIntercomJwt
  const intercomCompany = useMemo(() => {
    if (!organization) {
      return null
    }
    return getIntercomCompany(organization)
  }, [organization])

  const { isSmallScreen } = useCurrentBreakpoint()
  const isSmall = isSmallScreen()

  useEffect(() => {
    if (!isIntercomEnabled) {
      return
    }
    if (!EnvVariables.IntercomAppId) {
      // This if-statement exists to satisfy TypeScript that EnvVariables.IntercomApiBase is defined.
      // (If it were undefined, isIntercomEnabled would be false, but the type-checker does not know that.)
      return
    }
    Intercom({
      api_base: EnvVariables.IntercomApiBase,
      app_id: EnvVariables.IntercomAppId,
      custom_launcher_selector: `.${IntercomLauncherClassName}`,
      ...(intercomJwt ? { intercom_user_jwt: intercomJwt } : {}),
      ...(intercomCompany ? { company: intercomCompany } : {}),
      horizontal_padding: DefaultIntercomHorizontalPadding,
      hide_default_launcher: isSmall,
      ...(me
        ? {
            name: formatUserName(me), // Full name
            user_id: `${me.id}`, // a UUID for your user
            email: me.emailAddress ?? '', // the email for your user
            created_at: new Date(me.createdAt ?? '').getTime(), // Signup date as a Unix time in milliseconds
            IsGovStaff: me.organizationId ? 'True' : 'False',
            GovStaffGroup: me.group || null,
            GovStaffCity: organization?.city ?? '',
            Title: me.title ?? '',
            IsPlanReviewer: me.isPlanReviewer ? 'True' : 'False',
            IsInspector: me.isInspector ? 'True' : 'False',
            IsApplicant: !me?.organizationId ? 'True' : 'False',
          }
        : {}),
    })
  }, [
    me,
    stringifiedMe,
    isIntercomEnabled,
    intercomCompany,
    intercomJwt,
    organization?.city,
    isSmall,
  ])
}
